// apps/web/src/components/Account/Account.jsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PersonalInfoWeb from './PersonalInfoWeb';
import PublicProfileWeb from './PublicProfileWeb';
import UsageWeb from './UsageWeb';
import SecuritySettingsWeb from '../Security/SecuritySettingsWeb'; // keep as Security overview
import ChangePasswordWeb from '../Security/ChangePasswordWeb';
import TwoFactorEnableScreen from '@mysimpledb/shared/components/Security/TwoFactorEnable.web';
import RecoveryCodesWeb from '../Security/RecoveryCodesWeb';
import SensitiveVaultWeb from '../Security/SensitiveVaultWeb';
import SharingSettingsWeb from './SharingSettingsWeb';
import SettingsWeb from './SiteSettingsWeb';
import BillingWeb from './BillingWeb';
import SecurityOverview from '../Security/SecurityOverviewWeb';
import { useLocation } from 'react-router-dom';
import './Account.css';

export default function Account({ atab = '' }) {
  const { sub } = useParams(); // 'password' | '2fa' | 'recovery-codes' | undefined
  const [activeTab, setActiveTab] = useState('personal');
  const [securitySub, setSecuritySub] = useState('overview'); // 'overview' | 'password' | 'twofa' | 'recovery'

  const productCode = localStorage.getItem('productCode');
  const hasNotes = productCode === 'notes' || productCode === 'mysimpledb';
  const hasMySimpleDB = productCode === 'mysimpledb';

  const location = useLocation();

  const isNotesSite =
    window.location.hostname === 'mysimpledbnotes.com' ||
    window.location.hostname === 'www.mysimpledbnotes.com';

  // Notes users get Usage so they can view/manage SMS credit usage.
  // Preserve the existing MySimpleDB behavior: full-product users see Usage
  // only on the MySimpleDB site.
  const showUsage =
    productCode === 'notes' || (hasMySimpleDB && !isNotesSite);

  useEffect(() => {
    if (atab && atab !== activeTab) setActiveTab(atab);
  }, [atab]);

  useEffect(() => {
    if (sub) {
      setActiveTab('security');
      setSecuritySub(sub);
    }
  }, [sub]);

  const renderSecurityContent = () => {
    switch (securitySub) {
      case 'overview':
        return <SecurityOverview hasMySimpleDB={hasMySimpleDB} />;
      case 'password':
        return <ChangePasswordWeb />;
      case 'twofa':
        return <TwoFactorEnableScreen />;
      case 'recovery':
        return <RecoveryCodesWeb />;
      case 'vault':
        return <SensitiveVaultWeb />;
      default:
        return (
          <SecuritySettingsWeb onNavigate={(key) => setSecuritySub(key)} />
        );
    }
  };

  return (
    <div className="account-container">
      <div className="sidebar">
        <ul className="menu">
          <li
            className={activeTab === 'profile' ? 'active' : ''}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </li>

          {hasMySimpleDB && !isNotesSite && (
            <li
              className={activeTab === 'public-profile' ? 'active' : ''}
              onClick={() => setActiveTab('public-profile')}
            >
              Public Profile
            </li>
          )}

          {showUsage && (
            <li
              className={activeTab === 'usage' ? 'active' : ''}
              onClick={() => setActiveTab('usage')}
            >
              Usage
            </li>
          )}

          {/* Security parent with submenu */}
          <li
            className={`has-sub ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </li>
          {activeTab === 'security' && (
            <ul className="submenu" role="menu" aria-label="Security submenu">
              <li
                role="menuitem"
                className={securitySub === 'overview' ? 'active' : ''}
                onClick={() => setSecuritySub('overview')}
              >
                Overview
              </li>
              <li
                role="menuitem"
                className={securitySub === 'password' ? 'active' : ''}
                onClick={() => setSecuritySub('password')}
              >
                Password
              </li>
              <li
                role="menuitem"
                className={securitySub === 'twofa' ? 'active' : ''}
                onClick={() => setSecuritySub('twofa')}
              >
                Two-Factor Authentication
              </li>
              <li
                role="menuitem"
                className={securitySub === 'recovery' ? 'active' : ''}
                onClick={() => setSecuritySub('recovery')}
              >
                Two-Factor Recovery Codes
              </li>
              {hasMySimpleDB && !isNotesSite && (
                <li
                  role="menuitem"
                  className={securitySub === 'vault' ? 'active' : ''}
                  onClick={() => setSecuritySub('vault')}
                >
                  Sensitive Vault
                </li>
              )}
            </ul>
          )}

          {hasMySimpleDB && !isNotesSite && (
            <>
              <li
                className={activeTab === 'sharing' ? 'active' : ''}
                onClick={() => setActiveTab('sharing')}
              >
                Sharing & Visibility
              </li>
              <li
                className={activeTab === 'settings' ? 'active' : ''}
                onClick={() => setActiveTab('settings')}
              >
                Site Settings
              </li>
            </>
          )}
          <li
            className={activeTab === 'billing' ? 'active' : ''}
            onClick={() => setActiveTab('billing')}
          >
            Subscription
          </li>
        </ul>
      </div>

      <div className="account-content">
        {activeTab === 'profile' && <PersonalInfoWeb />}
        {activeTab === 'public-profile' && <PublicProfileWeb />}
        {activeTab === 'usage' && <UsageWeb />}
        {activeTab === 'security' && renderSecurityContent()}
        {activeTab === 'sharing' && <SharingSettingsWeb />}
        {activeTab === 'settings' && <SettingsWeb />}
        {activeTab === 'billing' && <BillingWeb />}
      </div>
    </div>
  );
}
